import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Dialog, DialogContent, Button } from '@mui/material';
import { mdiAccountCircleOutline, mdiMapMarker, mdiTextBoxOutline } from '@mdi/js';
import Icon from '@mdi/react';
import dayjs, { Dayjs } from 'dayjs';
import { useRecoilValue } from 'recoil';

import './CreateEventMenu.scss';
import { selectedDateAtom, useCalendarContext, useCreateEventMenuContext } from '../../../../data-access';
import { EventTimeSelector } from '../EventTimeSelector';
import { EventDescription } from '../EventDescription';
import { EventCategories } from '../EventCategories';
import { EnergyFeedbackType, EventCategorySet } from '@demind-inc/core';
import { CalendarAccountDropdown } from '../CalendarAccountDropdown';
import { isAppleCalendarDesktopSupported } from '../../../../helpers/appleCalendarDesktop.helpers';
import { EventEnergyFeedback } from '../EventEnergyFeedback';
import { canManageCalendarEventRecurrence } from '../helpers';
import { EventLocationAutocomplete } from '../EventLocationAutocomplete';
import { useTranslation } from 'react-i18next';

interface CreateEventMenuProps {
  visible: boolean;
  onClose: () => void;
}

const CreateEventMenu: React.FC<CreateEventMenuProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<Dayjs>(selectedDate);
  const [endDate, setEndDate] = useState<Dayjs>(selectedDate.add(30, 'minutes'));
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<EnergyFeedbackType>();
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
  const [selectedRecurrence, setSelectedRecurrence] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<EventCategorySet | null>(null);
  const { creatingEvent, handleCreateEvent, clearCreatingEvent } = useCreateEventMenuContext();
  const { mainCalendar, calendarAccountsToCreateEvents, findCalendarItem } = useCalendarContext();
  const [isAllDay, setIsAllDay] = useState<boolean>(false);
  const timeZone = isAllDay ? 'UTC' : dayjs.tz.guess();
  const isAppleDesktopSupported = isAppleCalendarDesktopSupported();

  useEffect(() => {
    if (!selectedCalendarId && mainCalendar?.calendarId) {
      setSelectedCalendarId(mainCalendar.calendarId);
    }
  }, [mainCalendar?.calendarId, selectedCalendarId]);

  useEffect(() => {
    if (!creatingEvent) {
      const defaultStartDate = selectedDate;
      const defaultEndDate = selectedDate.add(30, 'minutes');

      setTitle((prev) => (prev ? '' : prev));
      setDescription((prev) => (prev ? '' : prev));
      setLocation((prev) => (prev ? '' : prev));
      setSelectedCategory((prev) => (prev ? null : prev));
      setSelectedRecurrence((prev) => (prev ? null : prev));
      setSelectedFeedback((prev) => (prev ? undefined : prev));
      setIsAllDay((prev) => (prev ? false : prev));
      setStartDate((prev) => (prev.isSame(defaultStartDate) ? prev : defaultStartDate));
      setEndDate((prev) => (prev.isSame(defaultEndDate) ? prev : defaultEndDate));
      return;
    }

    const nextTitle = creatingEvent.summary || '';
    const nextDescription = creatingEvent.description || '';
    const nextLocation = creatingEvent.location || '';
    const nextStartDate = creatingEvent.start?.date
      ? dayjs(creatingEvent.start.date)
      : selectedDate;
    const nextEndDate = creatingEvent.end?.date
      ? dayjs(creatingEvent.end.date)
      : selectedDate.add(30, 'minutes');
    const nextRecurrence = (creatingEvent as any)?.recurrence?.string || null;

    setTitle((prev) => (prev !== nextTitle ? nextTitle : prev));
    setDescription((prev) => (prev !== nextDescription ? nextDescription : prev));
    setLocation((prev) => (prev !== nextLocation ? nextLocation : prev));
    setStartDate((prev) => (prev.isSame(nextStartDate) ? prev : nextStartDate));
    setEndDate((prev) => (prev.isSame(nextEndDate) ? prev : nextEndDate));
    setSelectedRecurrence((prev) => (prev !== nextRecurrence ? nextRecurrence : prev));
  }, [creatingEvent, selectedDate]);

  const selectedCalendarType = useMemo(() => {
    if (!selectedCalendarId) return null;
    return findCalendarItem(selectedCalendarId)?.calendarType || null;
  }, [findCalendarItem, selectedCalendarId]);

  const isSelectedAppleCalendar = selectedCalendarType === 'apple';
  const canManageRecurringEvent = useMemo(
    () => canManageCalendarEventRecurrence(selectedCalendarType, isAppleDesktopSupported),
    [isAppleDesktopSupported, selectedCalendarType]
  );

  const canSave = useMemo(
    () =>
      ((isAllDay && !!title.trim()) || (!!title.trim() && startDate.isBefore(endDate))) &&
      (!isSelectedAppleCalendar || isAppleDesktopSupported) &&
      !!selectedCalendarId,
    [
      isAppleDesktopSupported,
      isSelectedAppleCalendar,
      title,
      startDate,
      endDate,
      selectedCalendarId,
    ]
  );
  const canSelectEnergy = useMemo(() => dayjs(endDate).isSameOrBefore(dayjs()), [endDate]);

  const handleSaveEvent = () => {
    const trimmedLocation = location.trim();

    handleCreateEvent({
      summary: title,
      start: { date: startDate.toISOString(), timeZone },
      end: { date: endDate.toISOString(), timeZone },
      description,
      ...(trimmedLocation ? { location: trimmedLocation } : {}),
      categories: selectedCategory ? [selectedCategory] : [],
      calendarId: selectedCalendarId,
      eventMetrics: { energy: selectedFeedback },
      isAllDay,
      scheduleFrom: creatingEvent?.scheduleFrom || 'calendar',
      ...(creatingEvent?.recommendedTaskId
        ? { recommendedTaskId: creatingEvent.recommendedTaskId }
        : {}),
      ...(creatingEvent.activityFrom ? { activityFrom: creatingEvent.activityFrom } : {}),
      ...(canManageRecurringEvent && selectedRecurrence
        ? { recurrence: { string: selectedRecurrence } as any }
        : {}),
    });
    clearCreatingEvent();
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      (event.target as HTMLElement).closest('.event-description') ||
      (event.target as HTMLElement).closest('.event-location-autocomplete')
    ) {
      return;
    }

    if (!canSave) return;
    if (event.key === 'Enter') handleSaveEvent();
  };

  return (
    <Dialog
      open={visible}
      className="create-event-menu"
      onClose={onClose}
      disableRestoreFocus
      onKeyDown={(event) => {
        event.stopPropagation();
        handleKeyDown(event);
      }}
    >
      <DialogContent className="create-event-menu__content">
        <div className="create-event-menu__body">
          <TextField
            autoFocus
            value={title}
            placeholder={t('calendar.common.addTitle')}
            onChange={(e) => setTitle(e.target.value)}
            variant="standard"
            className="create-event-menu__title"
            fullWidth
          />
        <div className="create-event-menu__fields">
          <EventTimeSelector
            startDateTime={startDate}
            endDateTime={endDate}
            onStartTimeChange={(val) => setStartDate(val)}
            onEndTimeChange={(val) => setEndDate(val)}
            isAllDay={isAllDay}
            setIsAllDay={setIsAllDay}
            showRecurrenceSelector={canManageRecurringEvent}
            recurrenceValue={selectedRecurrence}
            onRecurrenceChange={setSelectedRecurrence}
          />
          <hr className="create-event-menu__divider" />
          <EventCategories
            className="create-event-menu__row"
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
          <hr className="create-event-menu__divider" />
          <div className="create-event-menu__row">
            <Icon path={mdiMapMarker} size="20px" className="create-event-menu__row__icon" />
            <EventLocationAutocomplete value={location} onChange={setLocation} />
          </div>
          <hr className="create-event-menu__divider" />
          <div className="create-event-menu__row create-event-menu__row--static">
            <Icon
              path={mdiAccountCircleOutline}
              size="20px"
              className="create-event-menu__row__icon"
            />
            <CalendarAccountDropdown
              calendars={calendarAccountsToCreateEvents}
              onSelectCalendarId={(id) => setSelectedCalendarId(id)}
              selectedCalendarId={selectedCalendarId}
              showUserIcon={false}
            />
          </div>
          <hr className="create-event-menu__divider" />
          <div className="create-event-menu__row create-event-menu__row--multiline">
            <Icon path={mdiTextBoxOutline} size="20px" className="create-event-menu__row__icon" />
            <EventDescription description={description} onChangeDescription={setDescription} hideToolbar />
          </div>
          {canSelectEnergy && (
            <>
              <hr className="create-event-menu__divider" />
              <EventEnergyFeedback
                selectedFeedback={selectedFeedback}
                onSelectFeedback={setSelectedFeedback}
              />
            </>
          )}
        </div>
        </div>
        <div className="create-event-menu__footer-sticky">
          <div className="create-event-menu__footer">
          <div className="create-event-menu__footer__actions">
            <Button
              onClick={onClose}
              variant="text"
              className="create-event-menu__footer__button create-event-menu__footer__button--cancel"
            >
              {t('calendar.common.cancel')}
            </Button>
            <Button
              disabled={!canSave}
              onClick={handleSaveEvent}
              variant="contained"
              className="create-event-menu__footer__button create-event-menu__footer__button--primary"
            >
              {t('calendar.common.save')}
            </Button>
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEventMenu;
